import { request } from "../../config/helpers/axios-instance";

/**
 * Every upstream call this flow makes lives here, so the endpoints can be
 * repointed in one place.
 *
 * Two of these are currently gated to the USER role on the backend
 * (`prescription/v2/validate` and `item/search`). They resolve locally because
 * `checkPermission` short-circuits on local env, and will 401 in dev/prod until
 * PHARMACIST/ADMIN/SUPER_SUPPORT are added to those routes.
 */

/**
 * Upload prescription files.
 *
 * The response carries two forms of each file: `image`/`pdf` is the bare stored
 * name, which is what validate() expects, and `url` is the full Azure link the
 * page can actually render. Both are needed — one won't do the other's job.
 */
export const uploadPrescription = async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const res = await request({
        url: "/file/upload/prescription/",
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
    });

    if (!res?.data?.success) {
        throw new Error(res?.data?.error || "Couldn't upload the prescription");
    }

    return (res?.data?.data || [])
        .map((item) => ({
            link: item.image || item.pdf,
            url: item.url || item.image || item.pdf,
        }))
        .filter((item) => item.link);
};

/** Run OCR + catalogue grounding over uploaded prescription links. */
export const validatePrescription = async ({ links, userMappingId }) => {
    const res = await request({
        url: "prescription/v2/validate",
        method: "POST",
        data: {
            links,
            ...(userMappingId ? { userMappingId: parseInt(userMappingId) } : {}),
            validationSource: "PILLENGINE_AUTOQUOTE",
        },
    });

    if (!res?.data?.success) {
        throw new Error(res?.data?.error || "Couldn't read the prescription");
    }

    return res?.data?.data || {};
};

/**
 * Catalogue type-ahead for lines the pharmacist resolves by hand.
 *
 * Results come straight from eVital, so the name here is the one the quotation
 * API can ground against — normalised to the shape the picker renders.
 */
export const searchMedicines = async (name) => {
    const res = await request({
        url: "item/search",
        method: "POST",
        data: { name },
    });

    const payload = res?.data?.data;
    const rows = payload?.result?.length ? payload.result : payload?.did_you_mean_result || [];

    return (Array.isArray(rows) ? rows : [])
        .map((row) => ({
            medicineId: row.medicine_id ?? row.id ?? null,
            name: row.medicine_name || row.name || "",
            content: row.content || row.salt_name || "",
            manufacturer: row.manufacturer_name || row.company_name || "",
            packing: row.pack_size || row.packing_size || "",
        }))
        .filter((row) => row.name);
};

/**
 * Create the quotation from the confirmed medicine lines.
 *
 * No `tenantId`: the API's setTenant middleware resolves it from the
 * `x-tenant-id` header and overwrites the body before validation runs. The
 * cookie holds the tenant's UUID, not the integer the schema wants, so anything
 * derived here would be wrong as well as ignored.
 */
export const createAutoQuote = async ({ userId, prescriptionId, medicineItems }) => {
    const res = await request({
        url: "quotation/admin/autoQuote",
        method: "POST",
        data: { userId: String(userId), prescriptionId, medicineItems },
    });

    if (!res?.data?.success) {
        throw new Error(res?.data?.error || "Couldn't create the quotation");
    }

    return res?.data?.data || {};
};

/**
 * Read the created quotation back, so the result step can name the lines that
 * actually landed. The backend drops lines it can't ground to eVital or that
 * have no inventory, and returns only a quotation id — so this call is the only
 * way to tell the pharmacist what made it in.
 */
export const getQuotationDetails = async (quotationId) => {
    const res = await request({
        url: `quotation/admin/orderDetails/${quotationId}`,
        method: "GET",
    });

    return res?.data?.data || null;
};
